import { useState, useEffect } from 'react';
import axiosClient from './lib/axiosClient';

const BUILT_IN_TYPES = [
  { name:'Text',         base_type:'text',        suffix:'', prefix:'', desc:'Any text value' },
  { name:'Number',       base_type:'number',       suffix:'', prefix:'', desc:'Numeric value' },
  { name:'Dropdown',     base_type:'select',       suffix:'', prefix:'', desc:'Pick from a list' },
  { name:'Yes/No',       base_type:'boolean',      suffix:'', prefix:'', desc:'True or false' },
  { name:'Date',         base_type:'date',         suffix:'', prefix:'', desc:'Calendar date' },
  { name:'Currency',     base_type:'currency',     suffix:'', prefix:'$', desc:'Dollar amount' },
  { name:'URL',          base_type:'url',          suffix:'', prefix:'', desc:'Web link' },
  { name:'Rating',       base_type:'rating',       suffix:'', prefix:'', desc:'1–5 stars' },
  { name:'Multi-select', base_type:'multiselect',  suffix:'', prefix:'', desc:'Pick multiple options' },
  { name:'Measurement',  base_type:'number',       suffix:'', prefix:'', desc:'Number with unit (set suffix)' },
];

const inp = { background:'#0e0e1a', border:'1px solid #2a2a3a', borderRadius:6, padding:'8px 11px', color:'#e0e0f0', fontSize:13, width:'100%', boxSizing:'border-box', outline:'none' };
const sel = { ...inp };
const lbl = { fontSize:11, color:'#666', fontWeight:600, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:4, display:'block' };

function Toggle({ on, onChange, label }) {
  return (
    <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',userSelect:'none'}}>
      <div onClick={onChange} style={{width:40,height:22,borderRadius:11,background:on?'#4a4aff':'#2a2a2a',position:'relative',transition:'background .2s',flexShrink:0,cursor:'pointer'}}>
        <div style={{position:'absolute',top:3,left:on?20:3,width:16,height:16,borderRadius:'50%',background:'#fff',transition:'left .2s'}} />
      </div>
      <span style={{fontSize:12,color:on?'#e0e0f0':'#555'}}>{label}</span>
    </label>
  );
}

export default function ColumnManager({ category, onSave, onClose }) {
  const [schema, setSchema] = useState(
    (category?.custom_field_schema || []).map(f => ({
      key: f.key||'', label: f.label||'', type_name: f.type_name||f.type||'Text',
      base_type: f.base_type||f.type||'text', suffix: f.suffix||'', prefix: f.prefix||'',
      options: f.options||[], required: f.required||false, show_in_table: f.show_in_table!==false,
    }))
  );
  const [customTypes, setCustomTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showTypeRegistry, setShowTypeRegistry] = useState(false);
  const [showNewType, setShowNewType] = useState(false);
  const [newType, setNewType] = useState({ name:'', base_type:'text', suffix:'', prefix:'', options:[], icon:'' });
  const [savingType, setSavingType] = useState(false);

  useEffect(() => {
    axiosClient.get('/api/rentals/field-types').then(r => setCustomTypes(r.data.types||[])).catch(()=>{});
  }, []);

  const allTypes = [...BUILT_IN_TYPES, ...customTypes.map(t=>({...t, isCustom:true}))];

  const applyType = (i, typeName) => {
    const t = allTypes.find(x=>x.name===typeName);
    if (!t) return;
    setSchema(s => { const n=[...s]; n[i]={...n[i], type_name:t.name, base_type:t.base_type, suffix:t.suffix||'', prefix:t.prefix||''}; return n; });
  };

  const update = (i, k, v) => setSchema(s => { const n=[...s]; n[i]={...n[i],[k]:v}; return n; });

  const add = () => setSchema(s => [...s, { key:'', label:'', type_name:'Text', base_type:'text', suffix:'', prefix:'', options:[], required:false, show_in_table:true }]);

  const remove = (i) => {
    const f = schema[i];
    if (f.key?.trim() && !window.confirm(`Delete column "${f.label||f.key}"?\n\nAny data stored in this field on existing units will be lost.`)) return;
    setSchema(s => s.filter((_,idx)=>idx!==i));
  };

  const moveUp   = (i) => { if(i===0) return; setSchema(s=>{const n=[...s];[n[i-1],n[i]]=[n[i],n[i-1]];return n;}); };
  const moveDown = (i) => { if(i===schema.length-1) return; setSchema(s=>{const n=[...s];[n[i],n[i+1]]=[n[i+1],n[i]];return n;}); };

  const handleSaveType = async () => {
    if (!newType.name.trim()) return alert('Type name is required');
    setSavingType(true);
    try {
      const r = await axiosClient.post('/api/rentals/field-types', newType);
      setCustomTypes(t=>[...t, r.data.type]);
      setNewType({ name:'', base_type:'text', suffix:'', prefix:'', options:[], icon:'' });
      setShowNewType(false);
    } catch(e) { alert(e.response?.data?.error||'Failed to save type'); }
    finally { setSavingType(false); }
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm('Delete this custom type? Existing fields using it will keep their data.')) return;
    try { await axiosClient.delete('/api/rentals/field-types/'+id); setCustomTypes(t=>t.filter(x=>x.id!==id)); }
    catch { alert('Could not delete'); }
  };

  const handleSave = async () => {
    for (const f of schema) {
      if (!f.key.trim()) return alert('Every column needs a field key (e.g. fuel_type)');
      if (!f.label.trim()) return alert('Every column needs a display label');
    }
    if (new Set(schema.map(f=>f.key)).size !== schema.length) return alert('Field keys must be unique');
    setSaving(true);
    try { await onSave(schema); onClose(); }
    catch { alert('Save failed'); }
    finally { setSaving(false); }
  };

  const needsOptions = (f) => ['Dropdown','Multi-select','select','multiselect'].includes(f.type_name)||['select','multiselect'].includes(f.base_type);

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#111',border:'1px solid #2a2a2a',borderRadius:14,width:'100%',maxWidth:680,maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.6)'}}>

        {/* Header */}
        <div style={{padding:'20px 24px',borderBottom:'1px solid #1a1a1a',flexShrink:0}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
            <div>
              <div style={{fontSize:17,fontWeight:700,color:'#f9fafb',marginBottom:2}}>⚙️ Manage Columns</div>
              <div style={{fontSize:12,color:'#555'}}>
                <span style={{color:'#7c6fcd',fontWeight:600}}>{category?.name}</span>
                {' '}— define what data gets tracked per unit in this category
              </div>
            </div>
            <button onClick={onClose} style={{background:'none',border:'none',color:'#555',fontSize:22,cursor:'pointer',lineHeight:1}}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{flex:1,overflowY:'auto',padding:'20px 24px'}}>

          {/* Column list */}
          {schema.length === 0 ? (
            <div style={{textAlign:'center',padding:'32px 0',color:'#444',fontSize:13,border:'1px dashed #252840',borderRadius:8,marginBottom:16}}>
              <div style={{fontSize:28,marginBottom:8}}>📊</div>
              <div style={{marginBottom:4,color:'#666'}}>No columns defined yet</div>
              <div style={{fontSize:12,color:'#444'}}>Click <strong style={{color:'#a0a0ff'}}>+ Add Column</strong> below to start tracking specs for {category?.name}</div>
            </div>
          ) : (
            <div style={{marginBottom:12}}>
              {schema.map((f,i)=>(
                <div key={i} style={{background:'#0c0c18',border:'1px solid #1a1a2a',borderRadius:10,padding:'14px 16px',marginBottom:10}}>
                  {/* Row 1: label + key + type */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 140px 160px',gap:10,marginBottom:10}}>
                    <div>
                      <label style={lbl}>Column Label *</label>
                      <input value={f.label} onChange={e=>{update(i,'label',e.target.value); if(!f.key||f.key===f.label.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,'').slice(0,-1)) update(i,'key',e.target.value.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,''));}}
                        placeholder="e.g. Fuel Type" style={inp} />
                    </div>
                    <div>
                      <label style={lbl}>Field Key *</label>
                      <input value={f.key} onChange={e=>update(i,'key',e.target.value.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,''))}
                        placeholder="fuel_type" style={{...inp,fontFamily:'monospace',fontSize:12}} />
                    </div>
                    <div>
                      <label style={lbl}>Type</label>
                      <select value={f.type_name} onChange={e=>applyType(i,e.target.value)} style={sel}>
                        <optgroup label="── Built-in ──">
                          {BUILT_IN_TYPES.map(t=><option key={t.name} value={t.name}>{t.name} — {t.desc}</option>)}
                        </optgroup>
                        {customTypes.length>0 && (
                          <optgroup label="── Your Custom Types ──">
                            {customTypes.map(t=><option key={t.id} value={t.name}>{t.icon?t.icon+' ':''}{t.name}{t.suffix?' ('+t.suffix+')':''}</option>)}
                          </optgroup>
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Options for dropdown */}
                  {needsOptions(f) && (
                    <div style={{marginBottom:10}}>
                      <label style={lbl}>Options (comma separated — e.g. 110v, 220v, Electric)</label>
                      <input 
                        value={f.options_raw !== undefined ? f.options_raw : (f.options||[]).join(', ')}
                        onChange={e=>update(i,'options_raw',e.target.value)}
                        onBlur={e=>{update(i,'options',e.target.value.split(',').map(x=>x.trim()).filter(Boolean));update(i,'options_raw',undefined);}}
                        placeholder="e.g. Gas, Diesel, Propane" style={inp} />
                    </div>
                  )}

                  {/* Suffix/prefix for measurement */}
                  {(f.type_name==='Measurement'||f.suffix||f.prefix) && (
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                      <div><label style={lbl}>Prefix (e.g. $)</label><input value={f.prefix||''} onChange={e=>update(i,'prefix',e.target.value)} placeholder="$" style={inp} /></div>
                      <div><label style={lbl}>Suffix (e.g. PSI, GPM)</label><input value={f.suffix||''} onChange={e=>update(i,'suffix',e.target.value)} placeholder="PSI" style={inp} /></div>
                    </div>
                  )}

                  {/* Row 2: toggles + actions */}
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
                    <div style={{display:'flex',gap:16}}>
                      <Toggle on={f.required} onChange={()=>update(i,'required',!f.required)} label="Required" />
                      <Toggle on={f.show_in_table} onChange={()=>update(i,'show_in_table',!f.show_in_table)} label="Show in table" />
                    </div>
                    <div style={{display:'flex',gap:4}}>
                      <button onClick={()=>moveUp(i)} title="Move up" style={{background:'#252840',border:'1px solid #2a2a3a',color:'#555',borderRadius:5,padding:'4px 8px',cursor:'pointer',fontSize:12}}>▲</button>
                      <button onClick={()=>moveDown(i)} title="Move down" style={{background:'#252840',border:'1px solid #2a2a3a',color:'#555',borderRadius:5,padding:'4px 8px',cursor:'pointer',fontSize:12}}>▼</button>
                      <button onClick={()=>remove(i)} title="Delete column" style={{background:'#2a0a0a',border:'1px solid #4a1a1a',color:'#f04040',borderRadius:5,padding:'4px 10px',cursor:'pointer',fontSize:12}}>Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button onClick={add} style={{background:'#252840',border:'1px dashed #3a3a5a',color:'#a0a0ff',borderRadius:8,padding:'10px',fontSize:13,cursor:'pointer',width:'100%',marginBottom:20}}>
            + Add Column
          </button>

          {/* Type Registry — collapsed by default */}
          <div style={{border:'1px solid #252840',borderRadius:8,overflow:'hidden'}}>
            <div onClick={()=>setShowTypeRegistry(v=>!v)} style={{padding:'10px 14px',cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center',background:'#1a1c2e'}}>
              <div style={{fontSize:12,fontWeight:600,color:'#555'}}>🗂 Custom Type Registry <span style={{fontSize:11,color:'#444',fontWeight:400}}>({customTypes.length} custom types)</span></div>
              <span style={{color:'#444',fontSize:12}}>{showTypeRegistry?'▲ Hide':'▼ Show'}</span>
            </div>
            {showTypeRegistry && (
              <div style={{padding:'14px 16px',background:'#090912'}}>
                <div style={{fontSize:12,color:'#444',marginBottom:12}}>Create reusable types with built-in suffix/prefix that appear in the type dropdown above.</div>

                {/* Existing custom types */}
                {customTypes.length > 0 && (
                  <div style={{marginBottom:12}}>
                    {customTypes.map(t=>(
                      <div key={t.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',background:'#0e0e1a',borderRadius:6,marginBottom:6,border:'1px solid #1a1a2a'}}>
                        {t.icon && <span>{t.icon}</span>}
                        <span style={{fontSize:13,color:'#e0e0f0',fontWeight:500,flex:1}}>{t.name}</span>
                        <span style={{fontSize:11,color:'#555'}}>{t.base_type}</span>
                        {t.suffix && <span style={{fontSize:11,color:'#7c6fcd',background:'#1a1a3a',padding:'2px 6px',borderRadius:4}}>+{t.suffix}</span>}
                        {t.prefix && <span style={{fontSize:11,color:'#7c6fcd',background:'#1a1a3a',padding:'2px 6px',borderRadius:4}}>{t.prefix}+</span>}
                        <button onClick={()=>handleDeleteType(t.id)} style={{background:'none',border:'none',color:'#f04040',cursor:'pointer',fontSize:12,padding:'2px 6px'}}>Delete</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Create new type */}
                <button onClick={()=>setShowNewType(v=>!v)} style={{background:'#252840',border:'1px dashed #2a2a4a',color:'#a0a0ff',borderRadius:6,padding:'7px 14px',fontSize:12,cursor:'pointer',marginBottom:showNewType?12:0}}>
                  {showNewType?'✕ Cancel':'+ Create New Type'}
                </button>
                {showNewType && (
                  <div style={{background:'#0e0e1a',border:'1px solid #2a2a3a',borderRadius:8,padding:'14px'}}>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 80px 80px 50px',gap:8,marginBottom:10}}>
                      <div><label style={lbl}>Type Name *</label><input value={newType.name} onChange={e=>setNewType(p=>({...p,name:e.target.value}))} placeholder="PSI Rating" style={inp} /></div>
                      <div><label style={lbl}>Base Behavior</label>
                        <select value={newType.base_type} onChange={e=>setNewType(p=>({...p,base_type:e.target.value}))} style={sel}>
                          {BUILT_IN_TYPES.map(t=><option key={t.name} value={t.base_type}>{t.name}</option>)}
                        </select>
                      </div>
                      <div><label style={lbl}>Suffix</label><input value={newType.suffix} onChange={e=>setNewType(p=>({...p,suffix:e.target.value}))} placeholder="PSI" style={inp} /></div>
                      <div><label style={lbl}>Prefix</label><input value={newType.prefix} onChange={e=>setNewType(p=>({...p,prefix:e.target.value}))} placeholder="$" style={inp} /></div>
                      <div><label style={lbl}>Icon</label><input value={newType.icon} onChange={e=>setNewType(p=>({...p,icon:e.target.value}))} placeholder="⚡" style={inp} /></div>
                    </div>
                    {(newType.base_type==='select'||newType.base_type==='multiselect') && (
                      <div style={{marginBottom:10}}>
                        <label style={lbl}>Default Options</label>
                        <input value={newType._optionsRaw !== undefined ? newType._optionsRaw : (newType.options||[]).join(', ')} 
                          onChange={e=>setNewType(p=>({...p,_optionsRaw:e.target.value}))}
                          onBlur={e=>setNewType(p=>({...p,options:e.target.value.split(',').map(x=>x.trim()).filter(Boolean),_optionsRaw:undefined}))}
                          placeholder="110v, 220v, Electric, Propane..." style={inp} />
                      </div>
                    )}
                    <button onClick={handleSaveType} disabled={savingType} style={{background:'#4a4aff',border:'none',color:'#fff',borderRadius:6,padding:'8px 18px',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                      {savingType?'Saving...':'Save Type'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{padding:'16px 24px',borderTop:'1px solid #1a1a1a',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
          <div style={{fontSize:12,color:'#444'}}>{schema.length} column{schema.length!==1?'s':''} · {schema.filter(f=>f.show_in_table).length} visible in table</div>
          <div style={{display:'flex',gap:10}}>
            <button onClick={onClose} style={{background:'transparent',border:'1px solid #333',color:'#9ca3af',borderRadius:6,padding:'9px 20px',fontSize:13,cursor:'pointer'}}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{background:'#4a4aff',border:'none',color:'#fff',borderRadius:6,padding:'9px 22px',fontSize:13,fontWeight:600,cursor:'pointer'}}>
              {saving?'Saving...':'Save Columns'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
